import { deleteIdentifier, fetchIdentifierTypes } from 'actions/identifier';
import ConfirmModal from 'components/ConfirmModal';
import Modal from 'components/Modal';
import SelectRedesign from 'components/Select/SelectRedesign';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { getIdentifierTypes } from 'selectors/identifier';
import { useSelector } from 'store';
import { NormalizedIdentifier } from 'types';

type ComponentProps = {
  isModalOpen: boolean;
  toggleModalOpen: () => void;
  handleEditOrCreate: (name: string, description: string, kind: string) => void;
  identifier?: NormalizedIdentifier;
  title?: string;
  isEditing?: boolean;
};

const EditIdentifierModal: React.FC<ComponentProps> = ({
  isModalOpen,
  toggleModalOpen,
  handleEditOrCreate,
  identifier,
  title,
  isEditing = false,
}) => {
  const dispatch = useDispatch();
  const [isDeletingIdentifier, setIsDeletingIdentifier] = useState(false);
  const [type, setType] = useState((identifier?.kind as string) || '');

  const types = useSelector(getIdentifierTypes);
  const typeOptions = Object.entries(types).map(([key, val]) => ({
    label: val.meta.name,
    value: key,
  }));

  const { register, getValues, handleSubmit, watch } = useForm<{
    name: string;
    description: string;
  }>({
    defaultValues: {
      name: identifier?.name === 'New identifier' ? '' : identifier?.name,
      description: identifier?.description || '',
    },
  });

  useEffect(() => {
    dispatch(fetchIdentifierTypes());
  }, [dispatch]);

  const deleteButtonStyle = 'text-litlingo-alert underline self-end focus:outline-none';

  const onSubmit = (): void => {
    const { name, description } = getValues();

    handleEditOrCreate(name, description, type);
  };

  const handleSaveButton = (): void => {
    handleSubmit(onSubmit)();
  };

  const toggleConfirmModal = (): void => setIsDeletingIdentifier(!isDeletingIdentifier);

  const handleDeleteClick = async (): Promise<void> => {
    if (identifier) {
      dispatch(deleteIdentifier({ identifierId: identifier.identifier_uuid, redirect: true }));
      toggleConfirmModal();
      toggleModalOpen();
    }
  };

  const getOkText = (): string => {
    if (isEditing) return 'Save';
    if (type === 'llm_categorizer') return 'Next';
    return 'Create';
  };

  const renderModelForm = (
    <div className="pt-8 pb-4">
      <form className="flex flex-col items-start">
        <div className="flex flex-col items-start gap-2 w-full">
          <div className="w-full flex flex-row gap-4">
            <label htmlFor="name" className="w-1/2 flex flex-col gap-1 text-body">
              <div className="flex flex-row gap-1">
                <span>Name</span>
                <span className="litlingo-red-color">*</span>
              </div>

              <input
                id="name"
                ref={register({ required: true, pattern: /\S/i })}
                name="name"
                placeholder={
                  identifier?.name === 'New identifier' ? 'New identifier' : 'Identifier Name'
                }
                className="form-input h-9 w-full p-2 text-body rounded placeholder-italic placeholder-litlingo-gray-6"
              />
            </label>

            <div className="w-1/2 flex flex-col gap-1 text-body">
              <div className="flex flex-row gap-1">
                <span>Type</span>
                <span className="litlingo-red-color">*</span>
              </div>
              <div className="w-full flex flex-row items-start gap-5">
                <div className="w-full flex-shrink-0">
                  <SelectRedesign
                    onChange={(value: string): void => setType(value)}
                    options={typeOptions}
                    value={type}
                    placeholder="Select Type"
                    dataTestid="model-type-select"
                    className="h-9"
                    disabled={isEditing}
                  />
                </div>
              </div>
            </div>
          </div>

          <label htmlFor="description" className="w-full flex flex-col gap-1 text-body">
            <div className="flex flex-row gap-1">
              <span>Description</span>
            </div>

            <textarea
              id="description"
              ref={register()}
              name="description"
              rows={3}
              placeholder="Use this description to help others understand the purpose of this identifier."
              className="form-input w-full p-2 text-body rounded resize-none placeholder-italic placeholder-litlingo-gray-6"
            />
          </label>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          body={renderModelForm}
          title={title || 'Edit Identifier Details'}
          okButton
          okButtonDisabled={!watch('name') || !type}
          okButtonText={getOkText()}
          okButtonOnClick={handleSaveButton}
          toggleShowModal={toggleModalOpen}
          secondaryButton={isEditing}
          secondaryButtonOnclick={toggleConfirmModal}
          secondaryButtonStyle={deleteButtonStyle}
          secondaryButtonText="Delete Identifier"
        />
      )}
      {isDeletingIdentifier && (
        <ConfirmModal
          data-testid="delete-modal"
          text="Are you sure you want to delete this identifier?"
          okButtonText="Yes"
          cancelButtonText="No"
          okButtonOnClick={handleDeleteClick}
          toggleShowModal={toggleConfirmModal}
        />
      )}
    </>
  );
};

export default EditIdentifierModal;
